/**
 * ChallengeInfoListItem Component
 * Renders a single item in the challenge info list with icon, label, and content
 */

import React, { memo } from 'react'
import { ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { useChallengeInfoStyles } from '../ChallengeInfo.styles'
import { ChallengeInfoListItemProps } from '../ChallengeInfo.types'

/**
 * Renders a single list item for challenge information
 * Uses memo to prevent unnecessary re-renders
 *
 * @param data - The item data containing label, content, and optional icon
 * @param index - List item index for React key
 * @param isDense - Whether to use dense (compact) styling
 */
const ChallengeInfoListItem: React.FC<ChallengeInfoListItemProps> = memo(({ data, index, isDense = false }) => {
  const classes = useChallengeInfoStyles()
  const { label, content, Icon } = data

  // Build class name based on density
  const listItemClassName = isDense ? `${classes.listItem} ${classes.listItemDense}` : classes.listItem

  return (
    <ListItem className={listItemClassName} key={index}>
      {/* Only render icon wrapper if icon exists */}
      {Icon && <ListItemIcon>{Icon}</ListItemIcon>}

      <ListItemText
        className={classes.listItemText}
        primary={label}
        secondary={content}
        primaryTypographyProps={{
          className: classes.listItemTextPrimary,
        }}
        secondaryTypographyProps={{
          className: classes.listItemTextSecondary,
          component: 'div',
        }}
      />
    </ListItem>
  )
})

ChallengeInfoListItem.displayName = 'ChallengeInfoListItem'

export default ChallengeInfoListItem
